import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ActualDataType,
    ComponentDefinition,
    ContextualField,
    DataType,
    Dictionary,
    DigitalCorrespondanceTable,
    DigitalSet,
    Group,
    IApplicationConfiguration,
    IFrequency,
    IIdentifierFormController,
    IValueViewModel,
    Identifier,
    IdentifierMiniature,
    IdentifierType,
    MeasuredIdentifierMiniature,
    Miniature,
    ObjectType,
    ReconciliationStatusIdentifierMiniature,
    TargetIdentifierMiniature,
    TimeInterval,
    TotalPenaltyIdentifierMiniature,
    ValidatedIdentifierMiniature,
    ConnectionPoint,
    ConnectionPointType
} from '../../../../models';
import { getMeasuredProperties } from './measuredIdentifierProperties';
import { getCalculatedProperties } from './calculatedIdentifierProperties';
import { getTargetProperties } from './targetIdentifierProperties';
import { getValidatedProperties } from './validatedIdentifierProperties';
import { IIntervalValidationService, ILoggerService, IStoreService } from '../../../../services';
import { identifierSaved, identifierSelected, guidEmpty } from '../../../../common';
import { StoreElement } from '../../../../stateManagement';
import { DataTypeField, DigitalSetGuid, variableProperties } from '../../../../common/helpers/contextual';
import { getDefaultFreq } from '../../../../services/helpers/getDefaultFreq';

class ModernIdentifierContextualController implements angular.IComponentController {
    correspondanceTables: { [guid: string]: DigitalCorrespondanceTable; };
    digitalSets: DigitalSet[];
    formController: IIdentifierFormController;
    groups: Group[];
    isLoaded: boolean;
    isLoading: boolean;
    miniatures: Miniature[];
    properties: ContextualField[];
    selectedGroup: Group;
    valueViewModels: Dictionary<IValueViewModel>;

    readonly appConfig: IApplicationConfiguration;
    readonly hasReconciliation: boolean;
    readonly selectedItem: Identifier;

    private _manualConnectionPoints: string[];
    private _original: Identifier;
    private _unsubscribe: Function;
    private _watchers: Function[] = [];

    static $inject = [
        '$rootScope',
        '$scope',
        '$timeout',
        'intervalValidationService',
        'loggerService',
        'storeService'
    ];

    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$scope: angular.IScope,
        private _$timeout: angular.ITimeoutService,
        private _intervalValidationService: IIntervalValidationService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService
    ) {
    }

    $onInit() {
        const miniatures = [
            new MeasuredIdentifierMiniature(),
            new IdentifierMiniature(),
            new ValidatedIdentifierMiniature(),
            new TargetIdentifierMiniature()
        ];
        if (this.hasReconciliation) {
            miniatures.push(new TotalPenaltyIdentifierMiniature());
            miniatures.push(new ReconciliationStatusIdentifierMiniature());
        }
        this.miniatures = miniatures;

        this.isLoaded = false;
        this.isLoading = true;

        this._unsubscribe = this._storeService.register(store => {
            const groups: Group[] = _.values(store.groups.dictionary);
            const digitalSets: DigitalSet[] = _.values(store.digitalSets.dictionary);

            this.digitalSets = digitalSets;
            this.groups = groups;
            this.correspondanceTables = store.digitalCorrespondanceTables.dictionary;
            this.valueViewModels = store.valueViewModels.dictionary;
            this._manualConnectionPoints = Object.values(store.connectionPoints.dictionary)
                .filter(cp => cp.type === ConnectionPointType.Manual)
                .map(d => d.guid);

            this.isLoaded = true;
            this.isLoading = false;

            if (this.selectedItem) {
                this.initSelection();
            }
        }, [
            StoreElement.ConnectionPoints,
            StoreElement.Groups,
            StoreElement.DigitalSets,
            StoreElement.DigitalCorrespondanceTables,
            StoreElement.ValueViewModels
        ]);
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['selectedItem'] && this.selectedItem) {
            this.initSelection();
        }
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
        this._watchers.forEach(w => w());
    }

    private initSelection() {
        // Free watchers and init backup
        this._watchers.forEach(w => w());
        this._watchers = [];
        this._original = angular.copy(this.selectedItem);
        // rebuild fields
        this.properties = this.rebuildFields();
        // reinit watchers
        this._watchers.push(this._$scope.$watch(
            () => this.selectedItem?.dataType,
            () => this.properties = this.rebuildFields()
        ));
    }

    private rebuildFields(): ContextualField[] {
        let properties: ContextualField[] = [];

        switch (this.selectedItem?.type) {
            case IdentifierType.Tag:
                properties = getMeasuredProperties(this.selectedItem);
                const vm = this.valueViewModels[this.selectedItem.tagDefinitionGuid];
                _.find(properties, p => p.modelName === 'tagDefinitionGuid')
                    .onChange = (viewValue: string, ea: IValueViewModel) => {
                        const updatedVm = this.valueViewModels[ea.guid];
                        this.updateCorrespondanceTablesProps(properties, updatedVm);
                    };
                this.updateCorrespondanceTablesProps(properties, vm);
                break;
            case IdentifierType.CalculatedValue:
                properties = this.getCalculatedProperties();
                break;
            case IdentifierType.Target:
                properties = getTargetProperties(this.selectedItem, this.selectedGroup);
                break;
            case IdentifierType.Validated:
                properties = getValidatedProperties(this.selectedItem, this.selectedGroup);
                break;
        }

        this.configureNameProperty(properties);

        this.configureDataTypeProperties(properties);

        return properties;
    }

    private configureDataTypeProperties(properties: ContextualField[]) {
        const dataTypeField = _.find(properties, p => p.modelName === DataTypeField.modelName);
        if (dataTypeField) {
            dataTypeField.onChange = v => this._$timeout(() => {
                const field = this.formController[DigitalSetGuid.propertyName];
                if (field) {
                    const value = field.$viewValue;
                    const isEmpty = !value || value === guidEmpty;
                    const isDigital = (v === DataType.Digital);
                    field.$setValidity('required', !(isDigital && isEmpty));
                }
            });
        }
    }

    private configureNameProperty(properties: ContextualField[]) {
        const nameProperty = _.find(properties, p => p.modelName === 'name');
        if (nameProperty) {
            nameProperty.isValid = (value) => {
                const required = !!value;
                let duplicate = true;
                if (this.selectedGroup && this.selectedGroup.identifierCollection.length > 0) {
                    duplicate = _.every(this.selectedGroup.identifierCollection, i => i.name !== value || i.guid === this.selectedItem.guid);
                }
                else {
                    if (this.groups?.length) {
                        const currentGroup = this.groups.find(group => group.identifierCollection.find(identifier => identifier.guid === this.selectedItem.guid));
                        duplicate = _.every(currentGroup.identifierCollection, i => i.name !== value || i.guid === this.selectedItem.guid);
                    } else {
                        duplicate = true;
                    }
                }
                return { required, duplicate };
            };
        }
    }

    private updateCorrespondanceTablesProps(properties: ContextualField[], vm: IValueViewModel) {
        const hideProperties = vm?.actualDataType !== ActualDataType.Digital || this._manualConnectionPoints.indexOf(vm.acquisitionPointGuid) < 0;
        const ctProp = _.find(properties, p => p.modelName === 'correspondanceTableGuid');
        const options = [{ key: guidEmpty, value: '' }];
        for (const key in this.correspondanceTables) {
            options.push({ key, value: this.correspondanceTables[key].name });
        }
        ctProp.extraData.options = options;
        ctProp.hide = hideProperties;

        const precProp = _.find(properties, p => p.modelName === 'precedentCorrespondanceIdentifierGuid');
        if (precProp) {
            const initialGuid = this.selectedItem.correspondanceTableGuid;
            precProp.hide = !initialGuid || initialGuid === guidEmpty || hideProperties;
            precProp.extraData.filter = (list: IValueViewModel[]) => {
                return this.filterViewModels(list, initialGuid);
            };

            ctProp.onChange = (viewValue: string) => {
                const ctGuid = viewValue;
                precProp.hide = !ctGuid || ctGuid === guidEmpty;
                precProp.extraData.filter = (list: IValueViewModel[]) => {
                    return this.filterViewModels(list, ctGuid);
                };
            };
        }
    }

    private filterViewModels(list: IValueViewModel[], ctGuid: string): IValueViewModel[] {
        const vm = this.valueViewModels[this.selectedItem.guid];
        let filtered = list.filter(d => {
            return d.actualDataType === ActualDataType.Digital
                && d.objectType === ObjectType.Identifier
                && d.guid !== vm?.guid;
        });

        if (ctGuid && ctGuid !== guidEmpty) {
            const ct = this.correspondanceTables[ctGuid];
            if (ct) {
                filtered = filtered.filter(d => {
                    return ct.layers.filter(l => l.digitalSetGuid === d.digitalSetGuid).length > 0;
                });
            }
        }
        if (vm) {
            let freq = vm.groupList[0].frequency;
            if (freq.interval === 0) {
                freq = { interval: this.appConfig.interval, intervalTimeUnit: this.appConfig.intervalTimeUnit };
            }
            filtered = filtered.filter(d => {
                const g = d.groupList[0];
                let groupFreq = g.frequency;
                if (groupFreq.interval === 0) {
                    groupFreq = { interval: this.appConfig.interval, intervalTimeUnit: this.appConfig.intervalTimeUnit };
                }
                return groupFreq.interval === freq.interval && groupFreq.intervalTimeUnit === freq.intervalTimeUnit;
            });
        }
        return filtered;
    }

    private getCalculatedProperties() {
        const defaultFrequency = getDefaultFreq(this.appConfig);
        const properties = getCalculatedProperties(this.selectedItem, this.selectedGroup, this.digitalSets, defaultFrequency);

        const linked = _.find(properties, p => p.modelName === 'linkedGuid');
        if (linked) {
            const oldLinkedOnChange = linked.onChange;
            linked.onChange = (guid: string, value: IValueViewModel) => {
                const result = oldLinkedOnChange(guid, value);
                variableProperties
                    .filter(p => this.formController[p.propertyName])
                    .map(p => ({
                        field: this.formController[p.propertyName] as angular.INgModelController,
                        model: p.modelName
                    }))
                    .filter(a => this.selectedItem[a.model] !== a.field.$modelValue
                        && !(this.selectedItem[a.model] === guidEmpty && !a.field.$modelValue)
                    )
                    .forEach(a => {
                        a.field.$setValidity('required', true);
                        a.field.$setDirty();
                    });
                return result;
            };
        }

        const aggregationPeriodField = this.buildAggregationField(properties);
        const isRollingField = _.find(properties, p => p.modelName === 'isRolling');
        if (!!isRollingField) {
            isRollingField.onChange = (viewValue: boolean) => {
                if (this.formController && this.formController[aggregationPeriodField.propertyName + 'Interval']) {
                    const formField: angular.INgModelController = this.formController[aggregationPeriodField.propertyName + 'Interval'];
                    const formField2: angular.INgModelController = this.formController[aggregationPeriodField.propertyName + 'intervalUnit'];
                    if (viewValue) {
                        formField.$setValidity('interval', true);
                        formField.$setValidity('nonRollingValue', true);
                        formField.$setValidity('rollingValue', this.validateAggregationPeriod(this.selectedItem.aggregationPeriod, viewValue));
                        formField2.$setValidity('interval', true);
                        formField2.$setValidity('nonRollingValue', true);
                        formField2.$setValidity('rollingValue', this.validateAggregationPeriod(this.selectedItem.aggregationPeriod, viewValue));
                    } else {
                        formField.$setValidity('interval', this._intervalValidationService.isValid(this.selectedItem.aggregationPeriod));
                        formField.$setValidity('rollingValue', true);
                        formField.$setValidity('nonRollingValue', this.validateAggregationPeriod(this.selectedItem.aggregationPeriod, viewValue));
                        formField2.$setValidity('interval', this._intervalValidationService.isValid(this.selectedItem.aggregationPeriod));
                        formField2.$setValidity('rollingValue', true);
                        formField2.$setValidity('nonRollingValue', this.validateAggregationPeriod(this.selectedItem.aggregationPeriod, viewValue));
                    }
                }
            };
        }
        return properties;
    }

    private buildAggregationField(properties: ContextualField[]) {
        const aggregationPeriodField = _.find(properties, p => p.modelName === 'aggregationPeriod');
        if (aggregationPeriodField) {
            if (this.selectedItem.isRolling) {
                aggregationPeriodField.extraData.frequencyValidationRules.push({
                    key: 'rollingValue',
                    isValid: freq => {
                        return this.validateAggregationPeriod(freq, this.selectedItem.isRolling);
                    }
                });
            }
            if (!this.selectedItem.isRolling) {
                aggregationPeriodField.extraData.frequencyValidationRules.push({
                    key: 'nonRollingValue',
                    isValid: freq => {
                        return this.validateAggregationPeriod(freq, this.selectedItem.isRolling);
                    }
                });
                aggregationPeriodField.extraData.frequencyValidationRules.push({
                    key: 'interval',
                    isValid: freq => {
                        return this.selectedItem.isRolling ? true : this._intervalValidationService.isValid(freq);
                    }
                });
            }
            aggregationPeriodField.messages = [
                { key: 'min', value: 'Invalid interval format. Interval must be an integer greater than 0.' },
                { key: 'format', value: 'Invalid interval format. Interval must be an integer greater than 0.' },
                { key: 'rollingValue', value: 'Invalid interval. Interval must be a multiple of the base identifier frequency.' },
                { key: 'nonRollingValue', value: 'Invalid interval. Interval must be a multiple of the aggregated identifier frequency.' },
                { key: 'interval', value: 'Unsupported time interval. Refer to section 7.2 of WiDE documentation for supported intervals.' },
                { key: 'required', value: 'A time unit is required.' }
            ];
            aggregationPeriodField.onChange = () => this.rebuildFields();
        }
        return aggregationPeriodField;
    }

    private validateAggregationPeriod(freq: IFrequency, isRolling: boolean) {
        let targetGroupFrequency = this.selectedGroup?.frequency;
        if (!targetGroupFrequency?.interval && this.appConfig) {
            targetGroupFrequency = { interval: this.appConfig.interval, intervalTimeUnit: this.appConfig.intervalTimeUnit };
        }
        const targetFrequency = freq?.interval > 0
            ? freq
            : targetGroupFrequency;
        const targetDuration = new TimeInterval(targetFrequency.interval, targetFrequency.intervalTimeUnit)
            .duration()
            .asSeconds();

        const sourceGroup = _.find(this.groups, g => g.identifierCollection.map(i => i.guid).indexOf(this.selectedItem.linkedGuid) >= 0);
        let isValid: boolean = false;
        if (sourceGroup) {
            let sourceGroupFrequency = sourceGroup.frequency;
            if (!sourceGroupFrequency?.interval && this.appConfig) {
                sourceGroupFrequency = { interval: this.appConfig.interval, intervalTimeUnit: this.appConfig.intervalTimeUnit };
            }
            const sourceDuration = new TimeInterval(sourceGroupFrequency.interval, sourceGroupFrequency.intervalTimeUnit)
                .duration()
                .asSeconds();
            if (!!isRolling) {
                isValid = targetDuration % sourceDuration === 0;
            } else {
                const targetGroupDuration = new TimeInterval(targetGroupFrequency.interval, targetGroupFrequency.intervalTimeUnit)
                    .duration()
                    .asSeconds();
                isValid = targetDuration % targetGroupDuration === 0;
            }
        }
        return isValid;
    }

    save(): angular.IPromise<Identifier> {
        const service = this._storeService.dispatchers().identifiers;
        const selectedItemGuid = this.selectedItem.guid;
        const groupFound = this.groups.filter(g => g.identifierCollection.filter(i => i.guid === selectedItemGuid).length > 0)[0];

        const promise = !groupFound
            ? service.add(this.selectedItem, this.selectedGroup)
            : service
                .update(this.selectedItem, groupFound)
                .then(i => {
                    this.selectedGroup = this.groups.filter(g => g.guid === groupFound.guid)[0];
                    return i;
                });

        promise
            .then(identifier => {
                this.formController.$setPristine();
                this.selectedItem.isCreating = false;
                this._$rootScope.$broadcast(identifierSaved, this.selectedItem, this.selectedGroup ? this.selectedGroup.guid : null);
                this._loggerService.logSuccess('Identifier successfully saved.');
                return identifier;
            })
            .catch(reason => {
                this._loggerService.logError(`Failed to save identifier : ${reason && reason.message ? reason.message : reason}`);
            });

        return promise;
    }

    cancel(): void {
        this.formController.$setPristine();
        this.selectedGroup.identifierCollection = this.selectedGroup.identifierCollection
            .filter(identifier => !identifier.isCreating);
        angular.copy(this._original, this.selectedItem);
        this._$rootScope.$broadcast(identifierSaved, this.selectedItem);
        this._$rootScope.$broadcast(identifierSelected, null);
    }
}

export const wtModernIdentifierContextual: ComponentDefinition = {
    componentId: 'wtModernIdentifierContextual',
    controller: ModernIdentifierContextualController,
    template: require('./modernIdentifierContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        appConfig: '<?',
        formController: '=?',
        hasReconciliation: '<?',
        selectedGroup: '=',
        selectedItem: '<',
    }
};